import {
    openModal,
    hideSpinner,
    showSpinner,
} from "@sitecorecomponents/BasicContent/Modal";
import { hasValidToken } from "@sitecorecomponents/Users/Store/tokenstore";
import { openSignup } from "../Signup/signup";
import { openForgotPassword } from "../ForgotPassword/forgotpassword";
import {
    emailRules,
    requiredRule,
    setupFieldValidation,
    setupPasswordToggle,
} from "../formValidation";
import { showFormMessage } from "../formMessages";
import { authenticateWithCredentials, AuthResult } from "../authApi";
import { refreshLoginState } from "./LoginStateElement";
import { resolveRedirectUrl } from "../redirect";

export type OpenLoginOptions = {
    /** Leave the current page when the login succeeds. Off by default: most callers
     *  (header button, apply-to-open-house modal) want the user to stay where they are. */
    redirect?: boolean;
};

function wireLoginLinks(
    form: HTMLFormElement,
    dialog: HTMLDialogElement | null,
) {
    form.querySelector<HTMLAnchorElement>("#open-signup")?.addEventListener(
        "click",
        (e) => {
            e.preventDefault();
            dialog?.close();
            openSignup();
        },
    );

    form.querySelector<HTMLAnchorElement>(
        "#open-forgot-password",
    )?.addEventListener("click", (e) => {
        e.preventDefault();
        dialog?.close();
        openForgotPassword();
    });
}

function setupLoginValidation(form: HTMLFormElement) {
    const email = form.querySelector<HTMLInputElement>("#Email");
    const password = form.querySelector<HTMLInputElement>("#Password");

    if (email) {
        setupFieldValidation(email, emailRules);
    }

    if (password) {
        setupFieldValidation(password, [requiredRule]);
        setupPasswordToggle(password);
    }
}

const createSubmitHandler =
    (options: OpenLoginOptions) => async (e: SubmitEvent) => {
        e.preventDefault();
        const form = e.currentTarget as HTMLFormElement;
        const email = (
            form.querySelector<HTMLInputElement>("#Email")?.value ?? ""
        ).trim();
        const password =
            form.querySelector<HTMLInputElement>("#Password")?.value ?? "";

        showFormMessage(form, null);

        let result: AuthResult;
        try {
            showSpinner();
            result = await authenticateWithCredentials(email, password);
        } finally {
            hideSpinner();
        }

        if (!result.success) {
            showFormMessage(form, result.reason ?? "request-failed");
            return;
        }

        if (options.redirect) {
            const redirectUrl = resolveRedirectUrl(form);
            if (redirectUrl) {
                document.location.assign(redirectUrl);
            } else {
                document.location.reload();
            }
            return;
        }

        refreshLoginState();
        form.closest("dialog")?.close();
    };

export const openLogin = async (options: OpenLoginOptions = {}) => {
    const form = document.createElement("form");

    return new Promise<boolean>((resolve) => {
        openModal({
            name: "log-ind",
            insertContent: (d) => {
                const innerHtml = d.innerHTML;
                d.innerHTML = "";

                form.innerHTML = innerHtml;
                form.onsubmit = createSubmitHandler(options);
                d.appendChild(form);
                setupLoginValidation(form);
                wireLoginLinks(form, d.closest("dialog"));
            },
            onClose: () => {
                form.onsubmit = null;
                resolve(hasValidToken());
            },
        });
    });
};
